/**
 * The landing page: what Morcast does, with an example campaign and its result. Its key figures.
 * Campaigns open now. How a campaign runs. What makes it trustworthy. What it offers brands and
 * creators. How anyone can check a result, and answers to common questions. The statements
 * follow the V1 specification (docs/specification.md). The answer about MOR points to the roadmap.
 */

import {
  ArrowRight,
  BookOpen,
  Check,
  CircleCheck,
  FileSearch,
  HandCoins,
  Lock,
  type LucideIcon,
  Megaphone,
  Plus,
  ScanSearch,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { campaignPath, useCampaigns } from "../campaigns/campaigns.js";
import {
  Aurora,
  Band,
  bandButtons,
  CallToActionBand,
  Eyebrow,
  glass,
  inset,
  SectionIntro,
  typeScale,
} from "../components/marketing.js";
import { Reveal } from "../components/Reveal.js";
import { styles } from "../components/ui.js";
import { CampaignCard } from "./campaigns/Browse.js";
import { PayoutExample, ProductPreview, SettlementExample, VerifyExample } from "./home/visuals.js";

/** The public protocol, which anyone can use to check Morcast's results. */
const PROTOCOL_URL = "https://github.com/Morphora-Lab/morcast-protocol";

const steps: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Lock,
    title: "The brand escrows a budget",
    text: "The budget and the performance target are locked in the Morcast escrow contract on Base, in USDC or MOR.",
  },
  {
    icon: Send,
    title: "Creators publish",
    text: "Any creator with a linked X account or YouTube channel can take part and submit posts during the campaign.",
  },
  {
    icon: ScanSearch,
    title: "Morcast verifies",
    text: "After the campaign ends, organic performance is measured and every post is checked against the brief. Paid promotion never counts.",
  },
  {
    icon: HandCoins,
    title: "The campaign settles",
    text: "The brand pays for the reach delivered, up to the budget. 80% goes to creators in proportion to their results and 20% to Morcast. The rest goes back to the brand.",
  },
];

/** What anyone can rely on, in short, under the hero's actions. */
const assurances: { icon: LucideIcon; label: string }[] = [
  { icon: Lock, label: "Budget escrowed on Base" },
  { icon: FileSearch, label: "Results published with evidence" },
  { icon: Wallet, label: "Payouts never expire" },
];

const figures = [
  { value: "80%", label: "of what a brand pays goes to creators" },
  { value: "100%", label: "of the budget not earned goes back to the brand" },
  { value: "0", label: "followers needed to take part as a creator" },
  { value: "5 to 10", unit: "days", label: "from a campaign's end to its settlement" },
];

const audiences = [
  {
    id: "for-brands",
    icon: Megaphone,
    title: "For brands",
    tagline: "Pay for delivered reach, never for promises.",
    points: [
      "Pay only for verified organic reach, never more than your budget.",
      "Get the whole budget back if nothing is delivered, or if the campaign is not settled within ten days of its end.",
      "Check every result. Each campaign's results are published with their evidence.",
    ],
    action: { to: "/campaigns/new", label: "Start a campaign" },
    guide: { to: "/brands", label: "How it works for brands" },
    visual: <SettlementExample />,
  },
  {
    id: "for-creators",
    icon: Sparkles,
    title: "For creators",
    tagline: "Earn from budgets that are already locked.",
    points: [
      "Take part in any campaign, with no follower minimums and no application.",
      "Earn from a budget that is already locked on-chain, in the campaign's token.",
      "Claim your payout whenever you like. It never expires.",
    ],
    action: { to: "/campaigns", label: "Find a campaign" },
    guide: { to: "/creators", label: "How it works for creators" },
    visual: <PayoutExample />,
  },
];

const proofs: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Lock,
    title: "The budget is locked first",
    text: "Every campaign's budget sits in an escrow contract on Base before creators publish.",
  },
  {
    icon: FileSearch,
    title: "Results come with evidence",
    text: "Each result is published with what was measured and checked, for anyone to verify.",
  },
  {
    icon: Check,
    title: "The settlement matches the result",
    text: "The escrow pays out exactly the published result, whose hash it records when the campaign settles.",
  },
];

const questions: { question: string; answer: ReactNode }[] = [
  {
    question: "What counts as delivered reach?",
    answer:
      "On X, it is the organic impressions of each accepted post. On YouTube, it is the organic views of each video, adjusted for how much of an integration segment viewers watched. Paid promotion never counts.",
  },
  {
    question: "How much does a brand pay?",
    answer:
      "The budget in proportion to the reach recognized, up to the whole budget once the target is reached. 80% goes to creators and 20% to Morcast. The rest of the budget goes back to the brand.",
  },
  {
    question: "Who can take part as a creator?",
    answer:
      "Anyone with an X account or a YouTube channel linked to their wallet. There is no follower minimum and no application. Publish content that follows the brief, with its disclosure, and submit it while the campaign runs. A creator may take part with up to 5 of their accounts in each campaign.",
  },
  {
    question: "How are results checked?",
    answer:
      "Morcast reads each item's organic performance with the creator's authorization after the campaign ends, and checks it against the brief, for edits and for signs of fraud. When it cannot decide alone, a person reviews the item. Every result is published with its evidence, and anyone can check it with the public Morcast Payment Protocol.",
  },
  {
    question: "When are creators paid?",
    answer:
      "A campaign settles between five and ten days after it ends. From then on, each creator claims their payout from the escrow whenever they like.",
  },
  {
    question: "What if a campaign is never settled?",
    answer:
      "If Morcast does not settle a campaign within ten days of its end, the brand can withdraw its whole budget from the escrow.",
  },
  {
    question: "Do I need MOR to use Morcast?",
    answer: (
      <>
        No. A brand funds its campaign in USDC or MOR, and creators are paid in the campaign's
        token. Nobody needs to hold or stake MOR today. MOR utility comes later, one step at a time
        as the network grows, as the{" "}
        <Link to="/docs/roadmap" className={styles.link}>
          roadmap
        </Link>{" "}
        sets out.
      </>
    ),
  },
];

/**
 * The tile of a step's or an audience's icon: the brand's gradient on the light theme. On the dark
 * theme it is blue-tinted glass with a blue icon, because a bright tile on near-black reads
 * as a light source and pulls the eye away from the text it introduces.
 */
const iconTile =
  "relative grid size-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-300 to-brand-500 text-ink-950 dark:from-brand-400/[0.16] dark:to-brand-400/[0.05] dark:text-brand-300 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.08)] dark:ring-1 dark:ring-brand-300/20 dark:ring-inset";

export function Home() {
  return (
    <>
      <title>Morcast · Pay creators for verified reach</title>

      <section className="relative isolate overflow-hidden">
        <Aurora />
        <div
          className={`${styles.frame} grid items-center gap-x-16 gap-y-16 pt-16 pb-20 sm:pt-24 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:pt-28 lg:pb-28`}
        >
          <Reveal>
            <Eyebrow>Creator campaigns, settled on Base</Eyebrow>
            <h1 className={`mt-7 ${typeScale.display}`}>
              Pay creators for{" "}
              <span className="text-gradient">the reach they actually deliver.</span>
            </h1>
            <p className={`mt-7 max-w-2xl ${typeScale.lead}`}>
              Brands escrow a campaign budget. Creators publish. Morcast measures organic
              performance, publishes the results for anyone to check, and settles on-chain, so
              brands pay only for verified delivery.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/campaigns/new" className={bandButtons.primary}>
                Start a campaign
                <ArrowRight aria-hidden className="size-4" />
              </Link>
              <Link to="/campaigns" className={bandButtons.secondary}>
                Browse campaigns
              </Link>
            </div>
            <ul className="mt-12 flex flex-wrap gap-x-7 gap-y-3 text-sm text-zinc-600 dark:text-zinc-400">
              {assurances.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2">
                  <Icon aria-hidden className="size-4 text-brand-600 dark:text-brand-400" />
                  {label}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={150}>
            <ProductPreview />
          </Reveal>
        </div>
      </section>

      <Band aria-label="Morcast in figures" innerClassName="pb-24 sm:pb-28">
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {figures.map((figure, index) => (
            <li key={figure.label}>
              <Reveal
                delay={index * 80}
                className={`relative h-full overflow-hidden p-5 sm:p-7 ${glass}`}
              >
                <div
                  aria-hidden
                  className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/50 to-transparent dark:via-brand-300/30"
                />
                <p className="font-display text-4xl font-semibold tracking-[-0.045em] tabular-nums sm:text-5xl">
                  <span className="text-gradient">{figure.value}</span>
                  {figure.unit && (
                    <span className="ml-1.5 text-xl font-medium tracking-tight text-zinc-500 sm:text-2xl dark:text-zinc-400">
                      {figure.unit}
                    </span>
                  )}
                </p>
                <p className="mt-3 text-sm leading-6 text-zinc-600 sm:text-base dark:text-zinc-400">
                  {figure.label}
                </p>
              </Reveal>
            </li>
          ))}
        </ul>
      </Band>

      <OpenCampaigns />

      <Band tone="raised" aria-labelledby="how-it-works" innerClassName="py-24 sm:py-32">
        <SectionIntro id="how-it-works" eyebrow="How it works" title="How a campaign works" center>
          Four steps from a locked budget to creators' payouts, each one recorded so that anyone can
          follow it.
        </SectionIntro>
        <div className="relative mt-16">
          <div
            aria-hidden
            className="absolute top-[3.25rem] right-[12%] left-[12%] hidden h-px bg-gradient-to-r from-brand-500/0 via-brand-500/40 to-brand-500/0 lg:block dark:from-white/0 dark:via-white/[0.12] dark:to-white/0"
          />
          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map(({ icon: Icon, title, text }, index) => (
              <li key={title}>
                <Reveal
                  delay={index * 100}
                  className={`group relative h-full p-6 transition duration-300 hover:-translate-y-1 hover:bg-white dark:hover:bg-ink-850/90 ${glass}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`${iconTile} shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_12px_32px_-12px_rgb(103_194_222/0.8)]`}
                    >
                      <Icon aria-hidden className="size-5" />
                    </span>
                    <span className="font-mono text-xs font-medium text-zinc-500 dark:text-zinc-400">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold tracking-[-0.02em] text-zinc-950 dark:text-white">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400">{text}</p>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </Band>

      <Features />

      <Band tone="raised" innerClassName="grid gap-5 py-24 sm:py-28 lg:grid-cols-2">
        {audiences.map((audience, index) => (
          <Reveal key={audience.id} delay={index * 120} className="h-full">
            <article
              aria-labelledby={audience.id}
              className={`group relative flex h-full flex-col overflow-hidden p-6 sm:p-10 ${glass}`}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-24 size-80 rounded-full bg-brand-300/20 blur-3xl transition-colors duration-500 group-hover:bg-brand-300/30 dark:bg-white/[0.03] dark:group-hover:bg-white/[0.05]"
              />
              <span className={`${iconTile} shadow-[inset_0_1px_0_rgb(255_255_255/0.5)]`}>
                <audience.icon aria-hidden className="size-6" />
              </span>
              <h2
                id={audience.id}
                className="relative mt-7 font-display text-3xl font-semibold tracking-[-0.03em] text-zinc-950 sm:text-4xl dark:text-white"
              >
                {audience.title}
              </h2>
              <p className="relative mt-2 text-lg text-zinc-600 dark:text-zinc-400">
                {audience.tagline}
              </p>
              <ul className="relative mt-7 space-y-3.5 text-zinc-700 dark:text-zinc-300">
                {audience.points.map((point) => (
                  <li key={point} className="flex gap-3">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-500/10 text-brand-700 ring-1 ring-brand-600/25 ring-inset dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/30">
                      <Check aria-hidden className="size-3" strokeWidth={3} />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
              <div className="relative mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
                <Link to={audience.action.to} className={styles.primaryButton}>
                  {audience.action.label}
                  <ArrowRight aria-hidden className="size-4" />
                </Link>
                <Link
                  to={audience.guide.to}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-700 transition-colors hover:text-zinc-950 dark:text-zinc-300 dark:hover:text-white"
                >
                  {audience.guide.label}
                  <ArrowRight aria-hidden className="size-3.5" />
                </Link>
              </div>
              <div className="relative mt-auto pt-10">{audience.visual}</div>
            </article>
          </Reveal>
        ))}
      </Band>

      <Band
        tone="deep"
        aria-labelledby="verify"
        innerClassName="grid items-center gap-14 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 -z-10 h-[32rem] w-[44rem] rounded-full bg-brand-300/20 blur-[120px] dark:bg-white/[0.025]"
        />
        <div>
          <SectionIntro
            id="verify"
            eyebrow="Open and verifiable"
            title="Every payout can be checked by anyone"
          >
            Morcast publishes each campaign's result with its evidence. Anyone can recompute every
            total and payout from it with the open-source Morcast Payment Protocol, and compare them
            with the escrow on Base.
          </SectionIntro>
          <ul className="mt-10 space-y-6">
            {proofs.map(({ icon: Icon, title, text }, index) => (
              <li key={title}>
                <Reveal delay={index * 90} className="flex gap-4">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white text-brand-700 shadow-sm ring-1 ring-zinc-900/[0.08] ring-inset dark:bg-white/[0.04] dark:text-brand-300 dark:shadow-none dark:ring-white/10">
                    <Icon aria-hidden className="size-5" />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-zinc-950 dark:text-white">
                      {title}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                      {text}
                    </p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ul>
          <a href={PROTOCOL_URL} rel="noopener" className={`mt-10 ${bandButtons.secondary}`}>
            <BookOpen aria-hidden className="size-4" />
            Read the Morcast Payment Protocol
          </a>
        </div>
        <Reveal delay={120}>
          <VerifyExample />
        </Reveal>
      </Band>

      <Band
        aria-labelledby="questions"
        innerClassName="grid gap-12 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]"
      >
        <Reveal>
          <h2 id="questions" className={typeScale.heading}>
            Questions
          </h2>
          <p className={`mt-5 ${typeScale.lead}`}>
            How reach is measured and checked, what a brand pays, and when creators are paid.
          </p>
          <p className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/brands" className={styles.link}>
              Guide for brands
            </Link>
            <Link to="/creators" className={styles.link}>
              Guide for creators
            </Link>
            <Link to="/docs" className={styles.link}>
              Docs
            </Link>
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div
            className={`divide-y divide-zinc-900/[0.07] overflow-hidden dark:divide-white/[0.07] ${glass}`}
          >
            {questions.map(({ question, answer }) => (
              <details
                key={question}
                className="group px-5 transition-colors open:bg-zinc-900/[0.02] sm:px-7 dark:open:bg-white/[0.02] [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 font-display font-medium text-zinc-900 sm:text-lg dark:text-zinc-100">
                  {question}
                  <span
                    aria-hidden
                    className="grid size-8 shrink-0 place-items-center rounded-full bg-zinc-900/[0.04] text-zinc-500 ring-1 ring-zinc-900/[0.08] transition duration-300 ring-inset group-open:rotate-45 group-open:bg-brand-500/10 group-open:text-brand-700 dark:bg-white/[0.05] dark:text-zinc-400 dark:ring-white/10 dark:group-open:bg-brand-400/15 dark:group-open:text-brand-300"
                  >
                    <Plus className="size-4" />
                  </span>
                </summary>
                <p className="pb-6 leading-7 text-zinc-600 dark:text-zinc-400">{answer}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </Band>

      <CallToActionBand
        title="Reach that is measured, checked and paid for on-chain."
        actions={
          <>
            <Link to="/campaigns/new" className={bandButtons.primary}>
              Start a campaign
              <ArrowRight aria-hidden className="size-4" />
            </Link>
            <Link to="/campaigns" className={bandButtons.secondary}>
              Find a campaign
            </Link>
          </>
        }
      >
        Start a campaign as a brand, or find one to take part in as a creator.
      </CallToActionBand>
    </>
  );
}

/** A tile of the features grid: what Morcast guarantees, beside a small illustration of it. */
function Feature({
  icon: Icon,
  title,
  text,
  className = "",
  delay = 0,
  children,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
  className?: string;
  delay?: number;
  children: ReactNode;
}) {
  return (
    <Reveal delay={delay} className={className}>
      <article
        className={`group relative flex h-full flex-col overflow-hidden p-6 transition duration-300 hover:bg-white sm:p-7 dark:hover:bg-ink-850/90 ${glass}`}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-40 w-72 -translate-x-1/2 rounded-full bg-brand-300/0 blur-3xl transition-colors duration-500 group-hover:bg-brand-300/20 dark:bg-white/0 dark:group-hover:bg-white/[0.04]"
        />
        <div aria-hidden className="relative mb-7 flex-1">
          {children}
        </div>
        <div className="relative flex items-start gap-3">
          <Icon aria-hidden className="mt-0.5 size-5 shrink-0 text-brand-600 dark:text-brand-400" />
          <div>
            <h3 className="font-display font-semibold tracking-[-0.01em] text-zinc-950 dark:text-white">
              {title}
            </h3>
            <p className="mt-1.5 text-sm leading-6 text-zinc-600 dark:text-zinc-400">{text}</p>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

/** What makes a campaign trustworthy, as a grid of tiles with small illustrations. */
function Features() {
  const checks = [
    'Disclosure "#ad" shown',
    "Link to the brand's page",
    "Hashtag #ColdBrew",
    "Logo from the brand kit visible",
  ];
  const tokens = ["USDC", "MOR"];
  return (
    <Band aria-labelledby="features" innerClassName="py-24 sm:py-32">
      <SectionIntro id="features" eyebrow="Built on proof" title="Nothing to take on trust">
        The budget waits in an escrow, every item is checked against the brief, and only organic
        reach is paid for.
      </SectionIntro>
      <div className="mt-14 grid gap-4 md:grid-cols-6">
        <Feature
          icon={Lock}
          title="Budgets locked before anyone publishes"
          text="Every campaign's budget sits in the Morcast escrow on Base. Morcast never holds it, and what is not earned goes back to the brand."
          className="md:col-span-3"
        >
          <div className={`rounded-2xl p-5 ${inset}`}>
            <div className="flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="font-mono">Escrow 0x9fe4…a6e0</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/[0.08] px-2 py-0.5 font-medium text-emerald-800 ring-1 ring-emerald-600/20 ring-inset dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/25">
                <span className="size-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                Funded
              </span>
            </div>
            <p className="mt-5 font-display text-3xl font-semibold tracking-[-0.03em] text-zinc-950 tabular-nums dark:text-white">
              10,000{" "}
              <span className="text-base font-medium text-zinc-500 dark:text-zinc-400">USDC</span>
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-[0.6875rem] text-zinc-500 dark:text-zinc-400">
              {["Deposited", "Running", "Settled"].map((stage, index) => (
                <div key={stage}>
                  <div
                    className={`h-1 rounded-full ${index < 2 ? "bg-gradient-to-r from-brand-500 to-brand-300" : "bg-zinc-900/10 dark:bg-white/10"}`}
                  />
                  <p className="mt-1.5">{stage}</p>
                </div>
              ))}
            </div>
          </div>
        </Feature>
        <Feature
          icon={ScanSearch}
          title="Every item checked against the brief"
          text="Required elements, the disclosure, and each content and visual requirement, with a person deciding what the checks cannot."
          className="md:col-span-3"
          delay={80}
        >
          <ul className="space-y-2">
            {checks.map((check) => (
              <li
                key={check}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-zinc-700 dark:text-zinc-300 ${inset}`}
              >
                {check}
                <CircleCheck className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              </li>
            ))}
          </ul>
        </Feature>
        <Feature
          icon={ShieldCheck}
          title="Organic reach only"
          text="Impressions and views from paid promotion never count."
          className="md:col-span-2"
          delay={0}
        >
          <div className="flex h-28 items-end gap-2">
            {[38, 56, 44, 72, 64, 88].map((height, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed illustration
                key={index}
                className="flex-1 rounded-t-md bg-gradient-to-t from-brand-500/40 to-brand-300 dark:from-brand-500/15 dark:to-brand-400/75"
                style={{ height: `${height}%` }}
              />
            ))}
            <div className="flex h-[60%] flex-1 items-center justify-center rounded-t-md bg-[repeating-linear-gradient(135deg,rgb(24_24_27/0.07)_0_6px,transparent_6px_12px)] ring-1 ring-zinc-900/10 ring-inset dark:bg-[repeating-linear-gradient(135deg,rgb(255_255_255/0.08)_0_6px,transparent_6px_12px)] dark:ring-white/10">
              <span className="text-[0.625rem] font-semibold tracking-wider text-zinc-500 uppercase dark:text-zinc-400">
                Ads
              </span>
            </div>
          </div>
        </Feature>
        <Feature
          icon={Users}
          title="Up to 5 accounts per creator"
          text="A creator takes part with up to five linked accounts in a campaign."
          className="md:col-span-2"
          delay={80}
        >
          <div className="flex h-28 items-center justify-center">
            <div className="flex -space-x-3">
              {[
                "from-sky-400 to-indigo-500",
                "from-violet-400 to-fuchsia-500",
                "from-emerald-300 to-teal-500",
                "from-amber-300 to-orange-500",
                "from-rose-400 to-pink-500",
              ].map((tone) => (
                <span
                  key={tone}
                  className={`size-12 rounded-full bg-gradient-to-br ring-4 ring-white dark:ring-ink-900 ${tone}`}
                />
              ))}
            </div>
          </div>
        </Feature>
        <Feature
          icon={Wallet}
          title="Paid in USDC or MOR, claimed any time"
          text="Payouts wait in the escrow until each creator claims them. They never expire."
          className="md:col-span-2"
          delay={160}
        >
          <div className="flex h-28 items-center justify-center gap-3">
            {tokens.map((token) => (
              <span
                key={token}
                className={`rounded-2xl px-5 py-4 font-display text-xl font-semibold text-zinc-950 dark:text-white ${inset}`}
              >
                {token}
              </span>
            ))}
          </div>
        </Feature>
      </div>
    </Band>
  );
}

/** Up to three campaigns open now or starting soon. Nothing when there are none. */
function OpenCampaigns() {
  const campaigns = useCampaigns();
  const open = (campaigns.data ?? [])
    .filter((campaign) => campaign.phase === "running" || campaign.phase === "upcoming")
    .slice(0, 3);
  if (open.length === 0) {
    return null;
  }
  return (
    <Band aria-labelledby="open-campaigns" innerClassName="pb-24 sm:pb-28">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionIntro id="open-campaigns" eyebrow="Open now" title="Campaigns open now" />
        <Link to="/campaigns" className={`inline-flex items-center gap-1.5 ${styles.link}`}>
          See all campaigns
          <ArrowRight aria-hidden className="size-4" />
        </Link>
      </div>
      <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {open.map((campaign) => (
          <CampaignCard key={campaignPath(campaign)} campaign={campaign} />
        ))}
      </ul>
    </Band>
  );
}
